/**
 * Enum e mappature dei valori restituiti dai servizi Workspace
 * (query searchItem / getItem, mutation updateItem).
 *
 * Come per baseTableKeys.js: queste stringhe non vanno mai scritte a mano nei
 * componenti, così un cambio lato Workspace si riflette in un solo punto.
 *
 * Fonte degli enum del blocco "antiMoneyLaundering": lo schema
 * AntiMoneyLaunderingResponse dello swagger di workspace-read
 * (https://workspace-read-dev.agyo.io/v3/api-docs). Le label italiane sono quelle
 * della pagina "Mappatura delle entità del Workspace e Storicizzazione"
 * (Confluence, spazio TeamWorksp).
 */

import {
    TIPO_DOCUMENTO_CARTA_IDENTITA_KEY,
    TIPO_DOCUMENTO_PASSAPORTO_KEY,
    TIPO_DOCUMENTO_PATENTE_GUIDA_KEY,
    TIPO_DOCUMENTO_PERMESSO_SOGGIORNO_KEY,
    TIPO_DOCUMENTO_PORTO_ARMI_KEY,
} from "./baseTableKeys";

/* Tipologia di soggetto restituita in "classifier" */
export const WS_CLASSIFIER_PERSON = "PERSON";
export const WS_CLASSIFIER_COMPANY = "COMPANY";
export const WS_CLASSIFIER_SELF_EMPLOYED_WORKER = "SELF_EMPLOYED_WORKER"; //Ditta individuale

/* Tipologia di indirizzo restituita in "offices[].officeType" */
export const WS_OFFICE_TYPE_MAIN = "MAIN";
export const WS_OFFICE_TYPE_SECONDARY = "SECONDARY";

/* Stato di item / personalData / offices */
export const WS_STATUS_ACTIVE = "ACTIVE";

/**
 * Mappatura fra l'enum "antiMoneyLaundering.politicalOfficeHeld" di Workspace e
 * le label dei ruoli politici usate dal form (baseTableApi.GetBasePoliticalRole).
 *
 * L'Autocomplete "ddlPepCarica" di AnagraficaPepSection confronta per stringa
 * esatta, quindi i valori a destra devono coincidere carattere per carattere
 * con quelli ritornati da GetBasePoliticalRole().
 *
 * I 15 enum sono quelli dichiarati dallo swagger e coprono tutte le 15 voci di
 * GetBasePoliticalRole(), nello stesso ordine dell'elenco a)...q) della pagina
 * Confluence. Un valore non mappato lascia il campo vuoto, non rompe nulla.
 *
 * Attenzione ai due "consigliere": REGIONAL_COUNTY_COUNCILLOR è la voce g)
 * "Consigliere regionale", REGIONAL_COUNCILLOR è la voce p) "Funzionario regionale".
 * L'abbinamento si ricava dall'ordine delle due liste e va confermato con il team
 * Workspace: se fosse invertito, l'impatto resta confinato a queste due cariche.
 */
export const WS_POLITICAL_OFFICE_TO_PEP_RUOLO = Object.freeze({
    PRESIDENT_OF_THE_REPUBLIC: "PRESIDENTE DELLA REPUBBLICA",
    PRIME_MINISTER: "PRIMO MINISTRO",
    MINISTER: "MINISTRO",
    DEPUTY_MINISTER: "VICE MINISTRO",
    UNDERSECRETARY: "SOTTOSEGRETARIO",
    PRESIDENT_OF_THE_REGION: "PRESIDENTE DELLA REGIONE",
    REGIONAL_COUNTY_COUNCILLOR: "CONSIGLIERE REGIONALE",
    MAYOR_OF_A_PROVINCE: "PRESIDENTE DELLA PROVINCIA",
    MAYOR_OF_A_MUNICIPALITY: "SINDACO",
    DEPUTY: "DEPUTATO",
    SENATOR: "SENATORE",
    PARLIAMENTARIAN: "PARLAMENTARE",
    EUROPEAN_PARLIAMENTARIAN: "PARLAMENTARE EUROPEO",
    REGIONAL_COUNCILLOR: "FUNZIONARIO REGIONALE",
    SIMILAR_OFFICES_FOREIGN_COUNTRIES: "UFFICIO DEGLI ESTERI",
});

/* Mappatura inversa, usata quando si spinge l'anagrafica verso Workspace */
export const WS_PEP_RUOLO_TO_POLITICAL_OFFICE = Object.freeze(
    Object.entries(WS_POLITICAL_OFFICE_TO_PEP_RUOLO).reduce(
        (acc, [wsEnum, pepRuolo]) => ({ ...acc, [pepRuolo]: wsEnum }),
        {}
    )
);

/**
 * Converte "politicalOfficeHeld" nella label attesa dal form.
 * Ritorna "" se il valore è assente o non mappato, così il campo resta vuoto
 * e selezionabile dall'utente invece di mostrare l'enum grezzo.
 */
export const GetPepRuoloFromWsPoliticalOffice = (politicalOfficeHeld) =>
    WS_POLITICAL_OFFICE_TO_PEP_RUOLO[politicalOfficeHeld] ?? "";

/**
 * Converte la label del form nell'enum atteso da Workspace.
 * Ritorna null se il valore è assente o non mappato.
 */
export const GetWsPoliticalOfficeFromPepRuolo = (pepRuolo) =>
    WS_PEP_RUOLO_TO_POLITICAL_OFFICE[pepRuolo] ?? null;

/**
 * "antiMoneyLaundering.verifiedWith" è il tipo di documento con cui il soggetto è
 * stato verificato, non la modalità di identificazione: la doc Workspace lo descrive
 * come "Verificato tramite ... Visura, Carta di identità, Passaporto", che è la label
 * dell'Autocomplete legato a docTipoId. Il corrispondente in procedura è quindi
 * base_tipo_documento, non base_tipo_identificazione (modalitaIdentId resta a
 * "Diretta": i valori di Workspace non hanno corrispondenza in quella tabella).
 */
export const WS_VERIFIED_WITH_OTHER = "OTHER";

/**
 * Mappatura fra i valori di "verifiedWith" e le chiavi di base_tipo_documento.
 * OTHER non compare qui di proposito: porta con sé la descrizione libera nel campo
 * "other", che va risolta sulle voci della tabella (comprese quelle del tenant).
 * Restano privi di corrispondente su Workspace "Visura camerale" e "Patente nautica".
 */
export const WS_VERIFIED_WITH_TO_TIPO_DOCUMENTO_KEY = Object.freeze({
    IDENTITY_CARD: TIPO_DOCUMENTO_CARTA_IDENTITA_KEY,
    PASSPORT: TIPO_DOCUMENTO_PASSAPORTO_KEY,
    RESIDENCY_PERMIT: TIPO_DOCUMENTO_PERMESSO_SOGGIORNO_KEY,
    DRIVERS_LICENSE: TIPO_DOCUMENTO_PATENTE_GUIDA_KEY,
    FIREARM_LICENSE: TIPO_DOCUMENTO_PORTO_ARMI_KEY,
});

/**
 * Converte "verifiedWith" nella chiave di base_tipo_documento corrispondente.
 * Ritorna null se il valore è assente, vale OTHER o non è mappato: in tutti questi
 * casi la riga va cercata altrove (campo "other") o si ricade sul default per
 * tipologia di soggetto.
 */
export const GetTipoDocumentoKeyFromWsVerifiedWith = (verifiedWith) =>
    WS_VERIFIED_WITH_TO_TIPO_DOCUMENTO_KEY[verifiedWith] ?? null;
